import Link from "next/link";
import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from "react";
import { Spinner } from "@/components/Spinner";

type ButtonProps = {
  variant?: "primary" | "secondary";
  size?: "md" | "sm";
  /** Disables the button and shows a spinner before the label. */
  pending?: boolean;
  /** Render as a Next.js <Link> with the same styles. */
  href?: string;
  children: ReactNode;
} & ButtonHTMLAttributes<HTMLButtonElement>;

const VARIANT: Record<NonNullable<ButtonProps["variant"]>, string> = {
  primary: "bg-green font-semibold text-surface",
  secondary: "border border-border bg-surface font-semibold text-ink-2",
};

const SIZE: Record<NonNullable<ButtonProps["size"]>, string> = {
  md: "px-5 py-2.5 text-[14px]",
  sm: "px-4 py-2 text-[13px]",
};

/**
 * Canonical action button (the pattern error.tsx / not-found / EmptyState used
 * to copy-paste). `pending` follows the LensClient convention: disabled +
 * spinner, label kept visible.
 */
export function Button({
  variant = "primary",
  size = "md",
  pending = false,
  href,
  className = "",
  style,
  disabled,
  children,
  ...rest
}: ButtonProps) {
  const cls =
    `inline-flex items-center justify-center gap-2 rounded-[12px] ${VARIANT[variant]} ${SIZE[size]} ` +
    `disabled:opacity-50 ${className}`;
  const merged: CSSProperties | undefined =
    variant === "primary" ? { boxShadow: "var(--shadow-cta)", ...style } : style;

  if (href) {
    return (
      <Link href={href} className={cls} style={merged}>
        {children}
      </Link>
    );
  }
  return (
    <button
      className={cls}
      style={merged}
      disabled={disabled || pending}
      aria-busy={pending || undefined}
      {...rest}
    >
      {pending && <Spinner size={15} />}
      {children}
    </button>
  );
}
